import { Button, Dialog, Text, VSpacer } from '@/components/DesignSystem';
import { PointsAuditApi } from '@/utilities/api/PointsAuditApi';
import { ApiPointsAuditLog } from '@api/interfaces';
import { Chip, CircularProgress, Stack } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { PointsAuditEndpoint } from '@api/endpoints';
import { DETAIL_DIALOG_WIDTH } from './auditLayout';
import { FarmerLedgerDialog } from './FarmerLedgerDialog';
import { describeAuditEvent } from './describeAuditEvent';
import { describeChanges } from './describeChanges';
import { eventTypeLabel } from './eventTypeLabel';
import {
  describeMovementReason,
  formatSignedPoints,
  rollUpReasons,
} from './movementReasons';
import { ProcessImpactTable } from './ProcessImpactTable';

interface AuditEventDetailDialogProps {
  event: ApiPointsAuditLog,
  onClose: () => void,
}

/** One audit row, plus who its process moved points for - which the row alone can't show. */
const RECALCULATING_POLL_MS = 3_000;

export const AuditEventDetailDialog = ({ event, onClose }: AuditEventDetailDialogProps) => {
  const { t } = useTranslation();
  const [selectedFarmer, setSelectedFarmer] =
    useState<PointsAuditEndpoint.GetProcess.ProcessImpact>();

  const { data: detail, isError, isFetching } = useQuery(
    ['points-audit-process-detail', event.processId],
    () => PointsAuditApi.getProcess(event.processId),
    {
      // The revert's recompute runs on the queue, so check back until it has finished.
      refetchInterval: (data) => (data?.isRevertRecalculating ? RECALCULATING_POLL_MS : false),
      retry: false,
    },
  );
  const isReverted = !!detail?.process?.isReverted;

  const field = (label: string, value: string | null | undefined) => (
    <Stack direction="row" gap="12px">
      <Text category="label-medium" sx={{ minWidth: 150 }}>{label}</Text>
      <Text category="body-medium">{value || '—'}</Text>
    </Stack>
  );

  const changes = () => {
    const lines = describeChanges(event.before, event.after, t);
    if (!lines.length) { return null; }

    return (
      <>
        <VSpacer size="4" />
        <Text category="label-medium">{t($ => $.pointsAudit.detail.whatChanged)}</Text>
        <VSpacer size="2" />
        {lines.map(({ key, text }) => (
          <Text category="body-medium" key={key}>{text}</Text>
        ))}
      </>
    );
  };

  const affected = () => {
    if (isFetching) {
      return <CircularProgress size={20} />;
    }
    if (isError) {
      return (
        <Text category="body-small" testID="audit-event-load-error">
          {t($ => $.pointsAudit.detail.loadError)}
        </Text>
      );
    }
    return (
      <ProcessImpactTable
        impact={detail?.impact ?? []}
        maxHeight={440}
        onSelectFarmer={setSelectedFarmer}
        reasons={detail?.reasons}
      />
    );
  };

  const revertMoved = () => {
    if (detail?.isRevertRecalculating) {
      return (
        <Stack alignItems="center" data-testid="revert-recalculating" direction="row" gap="8px">
          <CircularProgress size={16} />
          <Text category="body-small">{t($ => $.pointsAudit.detail.revertRecalculating)}</Text>
        </Stack>
      );
    }
    if (!detail?.revertImpact?.length) {
      return (
        <Text category="body-small">{t($ => $.pointsAudit.detail.revertMovedNothing)}</Text>
      );
    }
    return (
      <ProcessImpactTable
        impact={detail.revertImpact}
        maxHeight={280}
        onSelectFarmer={setSelectedFarmer}
      />
    );
  };

  return (
    <Dialog
      cancelButton={() => (
        <Button onClick={onClose} testID="audit-event-detail-close">
          {t($ => $.common.actions.close)}
        </Button>
      )}
      dialogWidth={DETAIL_DIALOG_WIDTH}
      onClose={onClose}
      open
      testID="audit-event-detail-dialog"
      title={t($ => $.pointsAudit.detail.title)}
    >
      <Stack>
        <Text category="title-small">{describeAuditEvent(event, t)}</Text>
        <VSpacer size="4" />

        {field(
          t($ => $.pointsAudit.detail.when),
          t($ => $.pointsAudit.dateTime, { date: event.createdAt }),
        )}
        {field(t($ => $.pointsAudit.detail.event), eventTypeLabel(event.eventType, t))}
        {field(t($ => $.pointsAudit.detail.who), event.actorName ?? event.actorType)}
        {field(t($ => $.pointsAudit.detail.why), event.reason)}
        {field(t($ => $.pointsAudit.detail.file), event.importFileName)}
        {isReverted && field(
          t($ => $.pointsAudit.detail.reverted),
          t($ => $.pointsAudit.detail.revertedWhenWho, {
            when: detail?.process.revertedAt,
            who: detail?.process.revertedBy ?? '—',
          }),
        )}

        {changes()}

        {!!detail?.reasons?.length && (
          <>
            <VSpacer size="5" />
            <Text category="label-medium">{t($ => $.pointsAudit.reason.whatMoved)}</Text>
            <VSpacer size="2" />
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {rollUpReasons(detail.reasons).map((total) => (
                <Chip
                  data-testid={`process-reason-${total.reason}`}
                  key={total.reason}
                  label={t($ => $.pointsAudit.reason.summary, {
                    count: total.farmersAffected,
                    points: formatSignedPoints(total.pointsDelta),
                    reason: describeMovementReason(total.reason, t),
                  })}
                  size="small"
                />
              ))}
            </Stack>
          </>
        )}

        <VSpacer size="5" />
        <Text category="label-medium">
          {isReverted
            ? t($ => $.pointsAudit.detail.affectedBeforeRevert)
            : t($ => $.pointsAudit.revert.affectedFarmers)}
        </Text>
        <VSpacer size="2" />
        {affected()}

        {isReverted && (
          <>
            <VSpacer size="5" />
            <Text category="label-medium">{t($ => $.pointsAudit.detail.revertMoved)}</Text>
            <VSpacer size="2" />
            {revertMoved()}
          </>
        )}

        {!!detail?.manufacturers?.length && (
          <>
            <VSpacer size="5" />
            <Text category="label-medium">{t($ => $.pointsAudit.detail.manufacturers)}</Text>
            <VSpacer size="2" />
            {detail.manufacturers.map((manufacturer) => (
              <Text category="body-medium" key={manufacturer.manufacturerId}>
                {t($ => $.pointsAudit.detail.manufacturerLine, {
                  count: manufacturer.farmersAffected,
                  name: manufacturer.name ?? manufacturer.manufacturerId,
                  points: formatSignedPoints(manufacturer.pointsDelta),
                })}
              </Text>
            ))}
          </>
        )}

        {!!selectedFarmer && (
          <FarmerLedgerDialog
            farmerId={selectedFarmer.farmerId}
            farmerName={selectedFarmer.name || selectedFarmer.businessName
              || selectedFarmer.externalId || selectedFarmer.farmerId}
            onClose={() => setSelectedFarmer(undefined)}
            processId={event.processId}
          />
        )}
      </Stack>
    </Dialog>
  );
};
